/**
 * QuoteReadinessSummary Component
 * 
 * Lightweight top-of-page summary bar showing estimate readiness.
 * Replaces the old persistent Pricing Integrity Warnings panel.
 * 
 * Shows:
 * - Ready / Not Ready status
 * - Blocked jobs count
 * - Needs review jobs count  
 * - Suggestions count
 * - Quick access to blocked jobs
 */

import React from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  ChevronRight,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

/**
 * Main Quote Readiness Summary Component
 */
export const QuoteReadinessSummary = ({
  readinessData,
  onViewBlockers,
  onJumpToJob,
  isLoading = false
}) => {
  if (!readinessData) return null;

  const {
    is_ready,
    blocked_jobs,
    needs_review_jobs,
    complete_jobs,
    total_jobs,
    total_suggestions,
    integrity_score,
    blocking_job_summaries
  } = readinessData;

  // Don't show if everything is complete and no suggestions
  const isAllClean = blocked_jobs === 0 && needs_review_jobs === 0 && total_suggestions === 0;
  
  if (isAllClean) {
    // Show minimal green confirmation
    return (
      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-green-800">
            Quote Ready
          </span>
          <span className="text-sm text-green-600">
            {complete_jobs}/{total_jobs} jobs complete
          </span>
        </div>
        {integrity_score !== undefined && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600">Integrity</span>
            <span className="text-sm font-semibold text-green-700">{integrity_score}%</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`p-3 rounded-lg border mb-4 ${
      blocked_jobs > 0 
        ? 'bg-red-50 border-red-200' 
        : needs_review_jobs > 0 
          ? 'bg-amber-50 border-amber-200'
          : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-center justify-between">
        {/* Left side - Status */}
        <div className="flex items-center gap-4">
          {/* Ready/Not Ready badge */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            is_ready 
              ? 'bg-green-100 text-green-700' 
              : 'bg-red-100 text-red-700'
          }`}>
            {is_ready ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {is_ready ? 'Ready to Send' : 'Not Ready'}
            </span>
          </div>

          {/* Counts */}
          <div className="flex items-center gap-3 text-sm">
            {blocked_jobs > 0 && (
              <div className="flex items-center gap-1">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-red-700 font-medium">{blocked_jobs} blocked</span>
              </div>
            )}
            
            {needs_review_jobs > 0 && (
              <div className="flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-amber-700">{needs_review_jobs} review</span>
              </div>
            )}
            
            {total_suggestions > 0 && (
              <div className="flex items-center gap-1 text-slate-500">
                <Lightbulb className="w-4 h-4" />
                <span>{total_suggestions} suggestions</span>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {integrity_score !== undefined && (
            <div className="flex items-center gap-1 mr-2">
              <span className="text-xs text-slate-500">Integrity</span>
              <span className={`text-sm font-semibold ${
                integrity_score >= 80 ? 'text-green-600' :
                integrity_score >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {integrity_score}%
              </span>
            </div>
          )}
          
          {blocked_jobs > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onViewBlockers}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Review Blockers
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Quick blockers list - only show if 1-3 blockers */}
      {blocked_jobs > 0 && blocked_jobs <= 3 && blocking_job_summaries && (
        <div className="mt-2 pt-2 border-t border-red-200">
          <div className="flex flex-wrap gap-2">
            {blocking_job_summaries.slice(0, 3).map((job, idx) => (
              <button
                key={job.job_id || idx}
                onClick={() => onJumpToJob?.(job.job_id)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-white border border-red-200 rounded hover:bg-red-50 transition-colors"
              >
                <XCircle className="w-3 h-3 text-red-500" />
                <span className="text-red-700 font-medium truncate max-w-[150px]">
                  {job.job_title}
                </span>
                <span className="text-red-500">({job.blocking_count})</span>
                <ChevronRight className="w-3 h-3 text-red-400" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteReadinessSummary;
